import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {InvestmentMetrics, LenderDetailsService} from '../service/lender-details.service';
import {Keys, StorageService} from '../service/storage.service';
import {ApiServiceService} from '../api-service.service';
import {ConfigService} from '../service/config.service';
import * as moment from 'moment';
import {LoanDetailsService} from '../service/loan-details.service';
import {Loan} from '../model/loan.model';
import { GraphData } from '../bar-graph/bar-graph.component';
import { AssetPerformanceService } from '../service/asset-performance.service';
import { Router } from '@angular/router';
import { PreferencesService } from '../service/preferences.service';
import { ShowTurnOffSwiftPopupUtil } from '../utils/finzy-flex/show-turn-off-swift-popup.utils';
import { UserAttributeInterface } from '../model/finzy-flex/user-attribute.model';
import { Toaster, Type } from '../toaster/Toaster';
import { LoanSettlementNegotiationService } from '../service/rbi-compliance/loan-settlement-negotiation.service';
import { LoanSettlementNegotiation } from '../model/rbi-compliance/loan-settlement-negotiation.model';

const lodash = require('lodash');

export interface AssetPerformanceList {
    asset_performance: [];
    asset_performance_info: [];
}

@Component({
    selector: 'app-investment-metrics',
    templateUrl: './investment-metrics.component.html',
    styleUrls: ['investment-metrics.component.css'],
    providers: [ApiServiceService]
})
export class InvestmentMetricsComponent implements OnInit {
    private preferencesService: PreferencesService;
    private readonly showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil;
    private userId: string;
    
    @Input()
    walletAmount: number;

    @Input()
    metrics: InvestmentMetrics;

    @Input()
    isCreateNewPortfolioDisabled: boolean;

    @Output()
    createNewPortfolioBtn : EventEmitter<boolean> = new EventEmitter();

    public assetPerformanceData: GraphData[];
    public lenderReturns;
    public upcomingReturns: { expectedEmiDate: Date, expectedEmiAmount: number };
    allocatedLoans: Loan[];

    private lenderDetailsService: LenderDetailsService;
    private storageService: StorageService;
    private configService: ConfigService;
    private loanDetailsService: LoanDetailsService;
    public networkCallInProgress: boolean;
    public createPortfolioAllowed: boolean = true;
    public fractionSize = '1.2-2';
    public assetPerformanceList: AssetPerformanceList;
    public assetPerformanceService : AssetPerformanceService;
    public assetPerformanceTooltipDescriptions: string[];
    private readonly router: Router;
    public loader: {spin: boolean} = {spin: false};

    private loanSettlementNegotiationService: LoanSettlementNegotiationService;
    public loanSettlementNegotiation: LoanSettlementNegotiation;

    // check is FINX user
    isFINXLender: boolean = false;

    constructor(lenderDetailsService: LenderDetailsService, storageService: StorageService, configService: ConfigService,
        loanDetailsService: LoanDetailsService, assetPerformanceService : AssetPerformanceService, router: Router, preferencesService: PreferencesService, showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil,
        loanSettlementNegotiationService: LoanSettlementNegotiationService) {
        this.router = router;
        this.lenderDetailsService = lenderDetailsService;
        this.storageService = storageService;
        this.configService = configService;
        this.loanDetailsService = loanDetailsService;
        this.networkCallInProgress = false;
        this.assetPerformanceService = assetPerformanceService;
        this.assetPerformanceData = [];
        this.assetPerformanceTooltipDescriptions = [];
        this.preferencesService = preferencesService;
        this.showTurnOffSwiftPopup = showTurnOffSwiftPopup;
        this.isFINXLender = this.storageService.getItem(Keys.FINX) === "true" && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true";
        this.loanSettlementNegotiationService = loanSettlementNegotiationService;
    }

    async ngOnInit(): Promise<void> {
        if(this.storageService.getItem(Keys.FINX) == 'true' && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true"){
            this.createPortfolioAllowed = this.configService.flexProFeatureOn();
        }
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        this.networkCallInProgress = true;
        this.assetPerformanceList = await this.assetPerformanceService.getAssetPerformanceList();
        this.getAssetPerformanceData(this.assetPerformanceList.asset_performance);
        this.getAssetPerformanceTooltipInfo(this.assetPerformanceList.asset_performance_info);
        this.allocatedLoans = await this.loanDetailsService.getAllocatedLoansFor(lenderId);
        this.lenderReturns = await this.lenderDetailsService.getLenderReturns(lenderId);
        this.getUpcomingLenderReturns();
        this.getLoanSettlementNegotiationByLenderId();
        this.networkCallInProgress = false;
        this.userId = this.storageService.getItem(Keys.PROFILE_ID);
    }


    getTotalReturnEarned(): number {
        const totalEarning = this.metrics.earnings;
        return Math.round((totalEarning.prepayment.interest + totalEarning.prepayment.principal + totalEarning.emi.interest + totalEarning.emi.principal + totalEarning.penalInterest + totalEarning.settlement.principal + totalEarning.settlement.interest + totalEarning.restructuring.principal + totalEarning.restructuring.interest) * 100) / 100;
    }

    getReturnedPrincipal(): number {
        const totalEarning = this.metrics.earnings;
        return Math.round((totalEarning.prepayment.principal + totalEarning.emi.principal + totalEarning.settlement.principal + totalEarning.restructuring.principal) * 100) / 100;
    }

    getReturnedInterest(): number {
        const totalEarning = this.metrics.earnings;
        return Math.round((totalEarning.prepayment.interest + totalEarning.emi.interest + totalEarning.penalInterest + totalEarning.settlement.interest + totalEarning.restructuring.interest) * 100) / 100;
    }

    getUpcomingLenderReturns(): void {
        const monthlyReturnsDate = this.configService.getMonthlyReturnsDate();
        const upcomingReturns = lodash.minBy(this.lenderReturns, lenderReturn => lenderReturn.expectedEmiDate);
        if (upcomingReturns) {
            const expectedEmiDate = new Date(upcomingReturns.expectedEmiDate);
            this.upcomingReturns = {
                expectedEmiDate: moment().date(monthlyReturnsDate).month(expectedEmiDate.getMonth()).year(expectedEmiDate.getFullYear()).toDate(),
                expectedEmiAmount: upcomingReturns.expectedEmiAmount
            };
        }
    }

    getAssetPerformanceTooltipInfo(assetPerformanceTooltipDescriptions) {
        assetPerformanceTooltipDescriptions.forEach(item => {
            item.placeholder.forEach(placeholderText => {
                item.text = item.text.replace("{}", `<strong> ${placeholderText} </strong>`);
            });
            this.assetPerformanceTooltipDescriptions.push(item.text);
        });
        return this.assetPerformanceTooltipDescriptions;
    }

    getAssetPerformanceData(assetPerformanceLists) {
        const roi = this.metrics ? this.metrics.roi : 0;
        const finzyRoi = {
            category: 'Finzy',
            value: roi,
            color: 'rgba(13, 58, 179, 1)'
        }
        for (const assetPerformanceList of assetPerformanceLists) {
            if(assetPerformanceList.category.length > 8 ){
                const label = assetPerformanceList.category.split(' ');
                assetPerformanceList.category = label;
            }
            assetPerformanceList['color'] = 'rgba(167, 167, 167, 1)';
        }
        this.assetPerformanceData = assetPerformanceLists;
        this.assetPerformanceData.push(finzyRoi);
        return this.assetPerformanceData;
    }

    returnsExists(): boolean {
        return !!this.lenderReturns && this.lenderReturns.length > 0;
    }

    getAllocatedLoansRouterLink(): void {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        this.router.navigate([`/lender/${lenderId}/allocated-loans`]);
    }

    async onClickCreateNewPortfolio() {
        if(this.isFINXLender) {
            return;
        }
        const preferences = await this.preferencesService.getUserPreferences(this.userId);
        if(this.showTurnOffSwiftPopup.isSwiftEnabled(preferences)) {
            try {
                const response: UserAttributeInterface = await this.showTurnOffSwiftPopup.showFinzySwiftPopup(preferences, this.loader, this.userId);
                this.loader.spin = false;
                if (response.status === 0) {
                    preferences.investmentAttrs = response.data[0].investmentPreferences;
                    Toaster.toast('Your swift preference switched off successfully!', Type.Success);
                    this.createNewPortfolioBtn.emit(true);
                }
                else if(response.status === 1){
                    Toaster.toast('Failed to update preference. Please try again after sometime.', Type.Error);
                }
            } catch(error) {
                Toaster.toast('Please try again', Type.Error);
                this.loader.spin = false;
            }
        }
        else {
            this.createNewPortfolioBtn.emit(true);
        }
    }

    goToWallet(): void {
        if(this.isFINXLender) {
            return;
        }
        this.router.navigate(['investorwallet']);
    }

    public navigateToLoanSettlementPage() {
        this.router.navigate(['/finzy-flex/loans/settlement']);
    }

    public async getLoanSettlementNegotiationByLenderId() {
        this.loanSettlementNegotiationService.getLoanSettlementNegotiationByLenderId(this.getLenderId())
          .then((res) => {
            this.loanSettlementNegotiation = res;
          })
          .catch((err) => {
            console.log(err)
          })
      }
    
      private getLenderId(): string {
        return this.storageService.getItem(Keys.LENDER_ID);
      }
}
